// ══════════════════════════════════════════════════════════════
// District88 — Global Search Service
// Searches across profiles, listings, and posts
// ══════════════════════════════════════════════════════════════

import { supabase } from '../lib/supabase';
import { ilikeFilter } from '../lib/ilikeEscape';
import type { Profile, Listing, Post } from '../types/database';

export interface SearchResults {
  profiles: Profile[];
  listings: (Listing & { seller: Profile })[];
  posts: (Post & { author: Profile })[];
}

export async function globalSearch(query: string, limit = 10): Promise<SearchResults> {
  const trimmed = query.trim();
  if (trimmed.length < 2) {
    return { profiles: [], listings: [], posts: [] };
  }

  const profileOr = [ilikeFilter('username', trimmed), ilikeFilter('display_name', trimmed)].join(',');
  const listingOr = [
    ilikeFilter('title', trimmed),
    ilikeFilter('description', trimmed),
    ilikeFilter('brand', trimmed),
  ].join(',');

  try {
    const [profilesRes, listingsRes, postsRes] = await Promise.all([
      supabase.from('profiles').select('*').or(profileOr).limit(limit),
      supabase
        .from('listings')
        .select('*, seller:profiles!seller_id(*)')
        .eq('status', 'active')
        .or(listingOr)
        .order('created_at', { ascending: false })
        .limit(limit),
      supabase
        .from('posts')
        .select('*, author:profiles!author_id(*)')
        .or(ilikeFilter('content', trimmed))
        .order('created_at', { ascending: false })
        .limit(limit),
    ]);

    if (profilesRes.error) console.warn('Profile search failed:', profilesRes.error.message);
    if (listingsRes.error) console.warn('Listing search failed:', listingsRes.error.message);
    if (postsRes.error) console.warn('Post search failed:', postsRes.error.message);

    return {
      profiles: profilesRes.error ? [] : ((profilesRes.data || []) as Profile[]),
      listings: listingsRes.error
        ? []
        : ((listingsRes.data || []) as (Listing & { seller: Profile })[]),
      posts: postsRes.error ? [] : ((postsRes.data || []) as (Post & { author: Profile })[]),
    };
  } catch (err) {
    console.warn('Search failed:', err);
    return { profiles: [], listings: [], posts: [] };
  }
}
